"use strict";

function IncidentToDoList() {
  function organizeNew(data) {
    return data.map(function (datum) {
      const requestedDate = datum.requestedDate ? DateTime.parseIsoDate(datum.requestedDate) : null;

      const result = {
        id: datum.id,
        name: datum.incidentName,
        tableIcon: IncidentsIcon.getIncidentsIconClass(
          datum.incidentStatus,
          datum.openEnforcements,
        ),
        address: datum.address,
        mileMarker: datum.mileMarker,
        highwayName: datum.highwayName,
        catchmentName: datum.catchmentName,
        requestingTypeDisplayName: _getRequestingTypeDisplayName(datum.requestingType),
        requestingAffiliationDisplayName: _getRequestingAffiliationDisplayName(
          datum.requestingAffiliation,
        ),
        requestedDate,
        displayRequestedDate: requestedDate ? DateTime.dateToGroupDisplayDate(requestedDate) : null,
        canCreateIncidentInvestigation: datum.canCreateIncidentInvestigation,
      };
      ToDoFunctions.addCommonFields(result, datum.incidentNew);
      return result;
    });
  }

  function organizeIncidentOpen(data) {
    return _organizeOpenSubject(data, "incidentOpen");
  }

  function organizeReinvestigation(data) {
    return _organizeOpenSubject(data, "incidentReinvestigation");
  }

  function organizeMaintenanceResponse(data) {
    return _organizeOpenSubject(data, "incidentMaintenanceResponse");
  }

  function _organizeOpenSubject(data, subjectKey) {
    return data.map(function (datum) {
      const followUpDate = datum.followUpDate ? DateTime.parseIsoDate(datum.followUpDate) : null;
      const subject = datum[subjectKey];

      const result = {
        id: datum.id,
        name: datum.incidentName,
        tableIcon: IncidentsIcon.getIncidentsIconClass(
          datum.incidentStatus,
          datum.openEnforcements,
        ),
        address: datum.address,
        mileMarker: datum.mileMarker,
        highwayName: datum.highwayName,
        catchmentName: datum.catchmentName,
        incidentTypeDisplayName: _getDisplayIncidentType(datum.incidentType),
        illicitActivityDisplayName: _getDisplayIllicitActivity(datum.illicitFlowMaterialActivity),
        followUpDate,
        followUpDatePassed: followUpDate ? DateTime.isPast(followUpDate) : false,
        displayFollowUpDate: followUpDate ? DateTime.dateToGroupDisplayDate(followUpDate) : null,
        canCreateIncidentInvestigation: datum.canCreateIncidentInvestigation,
      };
      ToDoFunctions.addCommonFields(result, subject);
      return result;
    });
  }

  function _getRequestingTypeDisplayName(requestingType) {
    return Misc.getOptionNameByValue(
      IncidentInventoryConstants.getRequestingTypeOptions(),
      requestingType,
    );
  }

  function _getRequestingAffiliationDisplayName(requestingAffiliation) {
    return Misc.getOptionNameByValue(
      IncidentInventoryConstants.getRequestingAffiliationOptions(),
      requestingAffiliation,
    );
  }

  function organizeFlaggedForNewEnforcement(data) {
    return data.map(function (datum) {
      const result = {
        id: datum.id,
        name: datum.incidentName,
        address: datum.address,
        responsiblePartyName: datum.responsiblePartyName,
        canIssueEnforcement: datum.canIssueEnforcement,
        tableIcon: IncidentsIcon.getIncidentsIconClass(
          datum.incidentStatus,
          datum.openEnforcements,
        ),
      };
      ToDoFunctions.addCommonFields(result, datum.incidentFlaggedForNewEnforcement);
      return result;
    });
  }

  function organizeOpenEnforcements(data) {
    const result = [];

    for (const datum of data) {
      const enforcements = datum.incidentOpenEnforcement?.additionalInfo ?? [];
      for (const enforcement of enforcements) {
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        const row = {
          id: datum.id,
          enforcementId: enforcement.id,
          name: datum.incidentName,
          address: datum.address,
          mileMarker: datum.mileMarker,
          incidentType: datum.incidentType,
          displayIncidentType: _getDisplayIncidentType(datum.incidentType),
          illicitActivity: datum.illicitActivity,
          displayIllicitActivity: _getDisplayIllicitActivity(datum.illicitActivity),
          displayEnforcementReason: _getDisplayEnforcementReason(
            enforcement.currentEscalation?.enforcementReason,
          ),
          displayLevel: _getDisplayLevel(enforcement.currentEscalation?.level),
          fine: enforcement.currentEscalation?.fine,
          issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : null,
          canCreateIncidentInvestigation: datum.canCreateIncidentInvestigation,
          tableIcon: IncidentsIcon.getIncidentsIconClass(
            datum.incidentStatus,
            datum.openEnforcements,
          ),
        };
        ToDoFunctions.addCommonFields(row, datum.incidentOpenEnforcement);
        result.push(row);
      }
    }

    return result;
  }

  function organizeRecommendedAction(data) {
    const result = [];

    for (const datum of data) {
      const enforcements = datum.incidentRecommendedAction?.additionalInfo ?? [];
      for (const enforcement of enforcements) {
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        const row = {
          id: datum.id,
          enforcementId: enforcement.id,
          name: datum.incidentName,
          address: datum.address,
          mileMarker: datum.mileMarker,
          incidentType: datum.incidentType,
          displayIncidentType: _getDisplayIncidentType(datum.incidentType),
          illicitActivity: datum.illicitActivity,
          displayIllicitActivity: _getDisplayIllicitActivity(datum.illicitActivity),
          displayEnforcementReason: _getDisplayEnforcementReason(
            enforcement.currentEscalation?.enforcementReason,
          ),
          displayLevel: _getDisplayLevel(enforcement.currentEscalation?.level),
          fine: enforcement.currentEscalation?.fine,
          issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : null,
          canCreateIncidentInvestigation: datum.canCreateIncidentInvestigation,
          displayRecommendedAction: BaseEnforcementAction().escalationActionOptions.find(
            (option) => option.value === enforcement.recommendedAction,
          )?.name,
          recommendedAction: enforcement.recommendedAction,
          tableIcon: IncidentsIcon.getIncidentsIconClass(
            datum.incidentStatus,
            datum.openEnforcements,
          ),
        };
        ToDoFunctions.addCommonFields(row, datum.incidentRecommendedAction);
        result.push(row);
      }
    }

    return result;
  }

  function _getDisplayIncidentType(incidentType) {
    return Misc.getOptionNameByValue(
      IncidentInvestigationConstants.getIncidentTypeOptions(),
      incidentType,
    );
  }

  function _getDisplayIllicitActivity(illicitActivity) {
    return illicitActivity ? "Yes" : "No";
  }

  function _getDisplayEnforcementReason(enforcementReason) {
    return Misc.getOptionNameByValue(
      FormSettings.getDropdownOptions("enforcement-reason", "idde"),
      enforcementReason,
    );
  }

  function _getDisplayLevel(level) {
    return Misc.getOptionNameByValue(
      FormSettings.getDropdownOptions("level", { tool: "indcom" }),
      level,
    );
  }

  function getSortersDict() {
    const dateSorter = DateTime.dateComparator;
    const alphaSorter = SortUtilities.alphaSorter;
    const numberSorter = SortUtilities.numberSorter;

    return {
      name: alphaSorter,
      address: alphaSorter,
      mileMarker: alphaSorter,
      highwayName: alphaSorter,
      catchmentName: alphaSorter,
      displayIncidentType: alphaSorter,
      displayIllicitActivity: alphaSorter,
      followUpDate: dateSorter,
      displayEnforcementReason: alphaSorter,
      displayLevel: alphaSorter,
      fine: numberSorter,
      issueDate: dateSorter,
      investigationDate: dateSorter,
      responsiblePartyName: alphaSorter,
      requestingTypeDisplayName: alphaSorter,
      requestingAffiliationDisplayName: alphaSorter,
      requestedDate: dateSorter,
      incidentTypeDisplayName: alphaSorter,
      illicitActivityDisplayName: alphaSorter,
      assignee: ToDoUserPicker.assigneeSort,
    };
  }

  function loadInvestigationListeners($container) {
    $container
      .off("click", ".new-incident-investigation", _onAddIncidentInvestigation)
      .on("click", ".new-incident-investigation", _onAddIncidentInvestigation);
  }

  function loadEnforcementListeners($container) {
    $container
      .off("click", ".js-edit-enforcement", _onEditEnforcement)
      .on("click", ".js-edit-enforcement", _onEditEnforcement);
  }

  function _onEditEnforcement(e) {
    e.stopPropagation();
    const enforcementId = $(this).data("id");
    const recommendedAction = $(this).data("recommended-action");
    IncidentEnforcementController.editEnforcementAction(
      enforcementId,
      null,
      {},
      recommendedAction ? { escalationAction: recommendedAction } : {},
    );
  }

  function loadFlaggedForEnforcementListeners($container, { selectedTable }) {
    const onFlaggedIncidentsClear = function (e) {
      e.stopPropagation();
      const checkedRowIds = selectedTable
        .getAllSelectedRows()
        .toArray()
        .map((elm) => $(elm).data("id"));

      IncidentToDoController.clearFlaggedIncidents(checkedRowIds);
    };

    $container.off("click", ".js-clear-flagged-incidents", onFlaggedIncidentsClear);
    $container.on("click", ".js-clear-flagged-incidents", onFlaggedIncidentsClear);

    $container.off("click", ".js-issue-incident-flagged-enforcement", _onIssueEnforcement);
    $container.on("click", ".js-issue-incident-flagged-enforcement", _onIssueEnforcement);
  }

  function _onIssueEnforcement() {
    const incidentId = $(this).data("incident-id");
    IncidentEnforcementController.loadNewEnforcement(incidentId);
  }

  function _onAddIncidentInvestigation() {
    const incidentId = $(this).parents("tr").data("id");
    const investigationType = $(this).data("investigation-type");
    IncidentInvestigationController.loadIncidentInvestigation({ incidentId, investigationType });
  }

  function canEnforceIncident(user) {
    return user.permissions.can_issue_incident_enforcement;
  }

  return {
    organizeNew,
    organizeIncidentOpen,
    organizeReinvestigation,
    organizeMaintenanceResponse,
    organizeFlaggedForNewEnforcement,
    organizeOpenEnforcements,
    organizeRecommendedAction,
    getSortersDict,
    loadInvestigationListeners,
    loadEnforcementListeners,
    loadFlaggedForEnforcementListeners,
    canEnforceIncident,
  };
}

module.exports = IncidentToDoList();

const BaseEnforcementAction = require("../../enforcement/baseEnforcementAction");
const DateTime = require("../../dateTime");
const FormSettings = require("../../settings/formSettings");
const IncidentsIcon = require("./incidentsIcon");
const Misc = require("../../misc");
const SortUtilities = require("../../general/sortUtilities");
const IncidentInvestigationConstants = require("../incidentInvestigation/incidentInvestigationConstants");
const IncidentInvestigationController = require("../incidentInvestigation/incidentInvestigationController");
const IncidentInventoryConstants = require("./incidentInventoryConstants");
const IncidentEnforcementController = require("../incidentEnforcement/incidentEnforcementController");
const IncidentToDoController = require("./incidentToDoController");
const ToDoFunctions = require("../../mapFunctions/toDoFunctions");
const ToDoUserPicker = require("../../general/toDoUserPicker");
